import React, { useEffect, useState } from "react";
import { Box, Typography, IconButton, Tooltip, Stack, Fade } from "@mui/material";
import { alpha } from "@mui/material/styles";
import LightModeIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeIcon from "@mui/icons-material/DarkModeOutlined";
import ScheduleIcon from "@mui/icons-material/ScheduleOutlined";
import InsightsIcon from "@mui/icons-material/InsightsOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLongOutlined";
import { useColorMode } from "../../theme/ColorModeProvider";
import SignInForm from "./SignInForm";
import SignUpForm from "./SignUpForm";
import BrandLogo from "../../components/layout/BrandLogo";

const HIGHLIGHTS = [
  {
    icon: ScheduleIcon,
    title: "Log time in seconds",
    body: "Weekly timesheets that fill themselves in from your projects and phases.",
  },
  {
    icon: InsightsIcon,
    title: "See where hours go",
    body: "Per-project and per-person reporting, ready whenever you need it.",
  },
  {
    icon: ReceiptLongIcon,
    title: "Bill without the busywork",
    body: "Turn approved time and expenses straight into client invoices.",
  },
];

/** Decorative brand panel — desktop only, hidden from assistive tech. */
function BrandPanel() {
  return (
    <Box
      aria-hidden
      sx={{
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        justifyContent: "space-between",
        width: "46%",
        maxWidth: 600,
        p: 6,
        position: "relative",
        overflow: "hidden",
        color: "common.white",
        bgcolor: "#0F172A",
      }}
    >
      {/* Soft accent glow rather than a hard gradient wash. */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: (t) =>
            `radial-gradient(circle at 20% 15%, ${alpha(t.palette.primary.main, 0.45)} 0%, transparent 55%),
             radial-gradient(circle at 85% 90%, ${alpha(t.palette.primary.light, 0.28)} 0%, transparent 50%)`,
        }}
      />
      {/* Faint grid, keeps the large flat area from looking empty. */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.5,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)," +
            "linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(circle at 50% 40%, #000 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(circle at 50% 40%, #000 30%, transparent 75%)",
        }}
      />

      <Box sx={{ position: "relative" }}>
        <BrandLogo height={30} color="common.white" />
      </Box>

      <Box sx={{ position: "relative", maxWidth: 420 }}>
        <Typography variant="h1" sx={{ mb: 1.5, color: "common.white" }}>
          Every hour, accounted for.
        </Typography>
        <Typography sx={{ color: alpha("#FFFFFF", 0.7), mb: 5 }}>
          Time tracking, expenses and invoicing for teams that bill by the hour.
        </Typography>

        <Stack spacing={3}>
          {HIGHLIGHTS.map(({ icon: Icon, title, body }) => (
            <Stack key={title} direction="row" spacing={2}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 2,
                  flexShrink: 0,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: alpha("#FFFFFF", 0.1),
                  border: 1,
                  borderColor: alpha("#FFFFFF", 0.12),
                }}
              >
                <Icon sx={{ fontSize: 19 }} />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ color: "common.white" }}>
                  {title}
                </Typography>
                <Typography variant="body2" sx={{ color: alpha("#FFFFFF", 0.62) }}>
                  {body}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      </Box>

      <Typography variant="caption" sx={{ position: "relative", color: alpha("#FFFFFF", 0.45) }}>
        © {new Date().getFullYear()} Geopro
      </Typography>
    </Box>
  );
}

export default function LoginPage() {
  const [mode, setMode] = useState("signin");
  const { resolvedMode, toggleMode } = useColorMode();

  // Landing here always ends the previous session.
  useEffect(() => {
    localStorage.clear();
  }, []);

  const isSignIn = mode === "signin";

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      <BrandPanel />

      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          position: "relative",
          // Crisp surface beside the dark brand panel, rather than the page plane.
          bgcolor: "background.paper",
          px: { xs: 3, sm: 6 },
          pt: { xs: "calc(24px + env(safe-area-inset-top))", sm: 4 },
          pb: { xs: "calc(24px + env(safe-area-inset-bottom))", sm: 4 },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {/* The logo lives in the brand panel on desktop, so repeat it only on mobile. */}
          <BrandLogo height={26} sx={{ display: { xs: "block", md: "none" } }} />
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title={resolvedMode === "dark" ? "Light mode" : "Dark mode"}>
            <IconButton onClick={toggleMode} sx={{ color: "text.secondary" }}>
              {resolvedMode === "dark" ? (
                <LightModeIcon sx={{ fontSize: 20 }} />
              ) : (
                <DarkModeIcon sx={{ fontSize: 20 }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 400, py: 4 }}>
            <Typography variant="h2" sx={{ mb: 1 }}>
              {isSignIn ? "Welcome back" : "Create your account"}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {isSignIn
                ? "Sign in to pick up where you left off."
                : "Get set up in under a minute."}
            </Typography>

            <Fade in key={mode} timeout={220}>
              <Box>
                {isSignIn ? (
                  <SignInForm onSwitchToSignUp={() => setMode("signup")} />
                ) : (
                  <SignUpForm onSwitchToSignIn={() => setMode("signin")} />
                )}
              </Box>
            </Fade>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
